import { forwardRef, inject, Injectable, resolveForwardRef, Type } from '@angular/core'
import { Router } from '@angular/router'
import { AssistanceEditorModalComponent, CreateEditorAssistanceProps } from './assistance-editor-modal.component'
import { Assistance } from '../../../models/assistance'
import { AlertController, ModalController } from '@ionic/angular'
import { AssistanceStatusName } from '../../../models/assistanceStatus'
import { AssistanceStatusManagerService } from '../../../services/assistance-status-manager.service'
import { BehaviorSubject, firstValueFrom, Observable, of, Subject, switchMap } from 'rxjs'
import { AssistanceService } from '../../../services/domain/assistance/assistance.service'
import { FastListAssistance } from '../../../models/FastListAssistance'
import { ParsedURID, RecurrenceUtil } from '../../recurrence/RecurrenceUtil'
import { EditorAssistance } from '../../../models/EditorAssistance'
import { FeathersService } from '../../../services/feathers.service'
import { filter, first, map, tap } from 'rxjs/operators'
import { EditorAssistanceBundle } from './EditorAssistanceBundle'
import { EditorAssistanceBundleBuilder } from './EditorAssistanceBundleBuilder'
import { FormStatus } from '../../../models/functional/formStatus'
import { AssistanceEditorService } from '../assistance-editor/assistance-editor.service'
import { CustomMethod } from '@feathersjs/client'
import { RecurrenceEditingMode } from '../../recurrence/recurrenceEditing'
import { PartialWithId } from '../../../shared/utils/typeUtils'
import {
  AssistanceEditorSavePopupComponent
} from '../assistance-editor/assistance-editor-save-popup/assistance-editor-save-popup.component'

export type AssistanceEditorModal = Type<AssistanceEditorModalComponent | AssistanceEditorSavePopupComponent>

@Injectable({
  providedIn: 'root',
})
export class AssistanceEditorModalService {
  private router = inject(Router)
  private modalCtrl = inject(ModalController)
  private alertCtrl = inject(AlertController)
  private assistanceStatusManager = inject(AssistanceStatusManagerService)
  private assistanceService = inject(AssistanceService)
  private feathers = inject(FeathersService)

  private TAG = 'AssistanceEditorService'

  private editorOpen = false
  private editorOpening = false
  private mainAssistanceDeletedInCurrentlyOpenEditor$$ = new Subject<void>()
  mainAssistanceDeletedInCurrentlyOpenEditor$ = this.mainAssistanceDeletedInCurrentlyOpenEditor$$.asObservable()

  private editorPreventAddBundleChild$$ = new BehaviorSubject(false)
  editorPreventAddBundleChild$: Observable<boolean> = this.editorPreventAddBundleChild$$.asObservable()

  formStatus = new FormStatus()

  isEditorOpen() {
    return this.editorOpen
  }

  // We use forwardRef to avoid circular references.
  // We cannot use modal class names instead of class references as the class names are minified in production.
  static ASSISTANCE_EDITOR_MODALS: AssistanceEditorModal[] = [
    forwardRef(() => AssistanceEditorModalComponent),
    forwardRef(() => AssistanceEditorSavePopupComponent),
  ]

  goToClient(clientId: number) {
    this.router.navigateByUrl(`/menu/app/tabs/clients/client/${clientId}/basic`)
  }

  setEditorPreventAddBundleChild(prevent: boolean) {
    this.editorPreventAddBundleChild$$.next(prevent)
  }

  async openById(assistanceId: number): Promise<HTMLIonModalElement> {
    // TODO: allow opening recurrent assistance, ie. open by URID ?
    // Open by URID:

    return await this.openEditor(await firstValueFrom(this.assistanceService.getById(assistanceId)))
  }

  async openEditor(
    assistanceProps: Assistance | FastListAssistance | CreateEditorAssistanceProps,
    recurrenceEditingMode?: RecurrenceEditingMode,
  ) {
    console.log(`${this.TAG}:openAssistanceEditor, editorOpen?`, this.editorOpen)
    if (this.editorOpen || this.editorOpening) {
      return
    }
    this.editorOpening = true
    this.formStatus.reset()

    const { AssistanceEditorModalComponent } = await import('./assistance-editor-modal.component')
    const modal = await this.modalCtrl.create({
      component: AssistanceEditorModalComponent,
      componentProps: {
        urid: (assistanceProps as any).URID,
        createProps: assistanceProps,
        recurrenceEditingMode,
      },
      backdropDismiss: false,
      id: AssistanceEditorModalComponent.name,
    })

    this.editorOpen = true
    this.editorOpening = false
    modal.onDidDismiss().then(() => {
      this.editorOpen = false
    })
    await modal.present()

    return modal
  }

  get(urid: string): Observable<EditorAssistance | null> {
    const parsedURID = RecurrenceUtil.parseURID(urid)
    if (parsedURID?.id) {
      return this.feathers.find(EditorAssistance, { id: parsedURID.id }).pipe(
        tap((aa) => console.log('AssistanceEditorModalService#get', urid, aa)),
        // The live store re-emits on every realtime event for the service, including ones where the
        // requested entity is no longer present (e.g. it was just deleted) — skip those instead of crashing.
        map((aa) => aa.find((a) => a.id === parsedURID.id)),
        filter((a): a is EditorAssistance => !!a),
      )
    } else {
      return of(null)
    }
  }

  getBundleByURID(urid: string, recurrenceEditingMode: RecurrenceEditingMode): Observable<EditorAssistanceBundle> {
    const parsedURID = RecurrenceUtil.parseURID(urid)
    if (!parsedURID) throw new Error(`${this.TAG}#getBundleByURID: Unparsable URID string ${urid}`)

    if (parsedURID.id) {
      return this.getNonrecurrentBundle(parsedURID.id)
    } else {
      return this.getRecurrentBundle(parsedURID, recurrenceEditingMode)
    }
  }

  mainAssistanceDeletedInCurrentlyOpenEditor() {
    this.closeAllModals()
    // this.mainAssistanceDeletedInCurrentlyOpenEditor$$.next()
  }

  async changeStatusWithDialog(
    assistance: Assistance,
    statusName: AssistanceStatusName,
    forceStatusChange = false,
    successFn?: (ass: Assistance) => void,
    presentFn?: () => void,
  ): Promise<HTMLIonAlertElement> {
    const targetStatus = this.assistanceStatusManager.getStatusByName(statusName)

    const alert = await this.alertCtrl.create({
      header: 'Změnit stav?',
      message: `Chcete změnit stav na "${targetStatus.humanReadableName}"?`,
      buttons: [
        {
          text: 'Ne',
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {
            alert.dismiss(false)
            return false
          },
        },
        {
          text: 'Ano',
          cssClass: 'primary',
          handler: async () => {
            const assistanceWithChangedStatus = await this.assistanceStatusManager.changeAssistanceStatus(
              assistance,
              statusName,
              forceStatusChange,
            )
            await successFn(assistanceWithChangedStatus)
          },
        },
      ],
    })

    await alert.present().then(presentFn)

    return alert
  }

  async closeAllModals() {
    for (const modal of AssistanceEditorModalService.ASSISTANCE_EDITOR_MODALS) {
      await this.closeModal(resolveForwardRef(modal))
    }
  }

  async closeModal(modal: AssistanceEditorModal) {
    try {
      await this.modalCtrl.dismiss(null, null, modal.name)
    } catch (e) {
      console.log('Unable to dismiss', e)
    }
  }

  /**
   * Converts an existing assistance into a group session (skupinovka) in place — just flips the `isGroupSession` flag
   * on the assistance. Unlike {@link createBundleChild}, it does NOT add a member, so the primary client stays the
   * sole member; further clients are added (each with their own client) from the group-session clients panel.
   */
  async convertToGroupSession(mainAssistance: EditorAssistance): Promise<EditorAssistance> {
    return this.feathers.patchByUrid(EditorAssistance, { urid: mainAssistance.URID, isGroupSession: true })
  }

  async createBundleChild(
    mainAssistance: EditorAssistance,
    isGroupSession: boolean,
  ): Promise<PartialWithId<Assistance>> {
    return this.feathers
      .serviceWithCustomMethods<{
        createBundleChild: CustomMethod
      }>('editor-assistances')
      .createBundleChild({ urid: mainAssistance.URID, isGroupSession })
  }

  /**
   * Retrieves a non-recurrent assistance bundle based on the given assistance ID.
   *
   * @param assistanceId - id of *any* assistance in the bundle
   * @return An observable of assistance bundle.
   */
  private getNonrecurrentBundle(assistanceId: number): Observable<EditorAssistanceBundle> {
    return this.feathers.find(EditorAssistance, { id: assistanceId }).pipe(
      first(),
      map((exactIdMatchAssistance) => exactIdMatchAssistance.filter((a) => a.id === assistanceId)[0]),
      switchMap((a) => {
        if (a.bundleParentId) {
          return this.feathers
            .find(EditorAssistance, {
              $or: [{ id: a.bundleParentId }, { bundleParentId: a.bundleParentId }],
            })
            .pipe(
              map((assistances) => {
                const bundleBuilder = new EditorAssistanceBundleBuilder()
                bundleBuilder.setMain(assistances.find((assistance) => assistance.id === a.bundleParentId))
                bundleBuilder.addChildren(
                  assistances
                    .filter((assistance) => assistance.bundleParentId === a.bundleParentId)
                    .sort(function (a, b) {
                      return a.id - b.id
                    }),
                )
                bundleBuilder.setActive(assistanceId)
                return bundleBuilder.build()
              }),
            )
        } else {
          return this.feathers
            .find(EditorAssistance, {
              $or: [{ id: assistanceId }, { bundleParentId: assistanceId }],
            })
            .pipe(
              map((assistances) => {
                const bundleBuilder = new EditorAssistanceBundleBuilder()
                bundleBuilder.setMain(assistances.find((assistance) => assistance.id === assistanceId))
                bundleBuilder.addChildren(
                  assistances
                    .filter((assistance) => assistance.bundleParentId === assistanceId)
                    .sort(function (a, b) {
                      return a.id - b.id
                    }),
                )
                return bundleBuilder.build()
              }),
            )
        }
      }),
      tap((assistances) => console.log('AssistanceEditorModalService#getBundleByURID', assistances)),
    )
  }

  private getRecurrentBundle(
    urid: ParsedURID,
    recurrenceEditingMode: RecurrenceEditingMode,
  ): Observable<EditorAssistanceBundle> {
    if (!urid.recurrenceParentId) throw new Error(`Bad URID / should be recurrent: ${urid.id}`)

    const isSplitting = recurrenceEditingMode === RecurrenceEditingMode.currentOccurrenceAndFollowing

    // Reload an already-materialized occurrence by id instead of regenerating a blank virtual one.
    return this.feathers
      .find(EditorAssistance, {
        recurrenceParentId: urid.recurrenceParentId,
        recurrenceExceptionDate: urid.plannedFrom,
      })
      .pipe(
        first(),
        map((existing) => existing.filter((a) => a.recurrenceParentId === urid.recurrenceParentId)),
        switchMap((existing) => {
          if (existing.length) return this.getNonrecurrentBundle(existing[0].id)

          return this.feathers.find(EditorAssistance, { id: urid.recurrenceParentId }).pipe(
            tap((e) => console.log('received recurrent bundle', e)),
            map((aa) => aa.filter((a) => a.id === urid.recurrenceParentId)[0]),
            map((a) => {
              const occurrence = a.generateOccurrence(urid.plannedFrom)
              if (isSplitting) occurrence.rrule = a.rrule
              return occurrence
            }),
            map((a) => {
              console.log('AssistanceEditorModalService#getBundleByURID', a)
              const bundleBuilder = new EditorAssistanceBundleBuilder()
              bundleBuilder.setMain(a)
              return bundleBuilder.build()
            }),
          )
        }),
      )
  }
}
